import { forEach, isNil, keyBy, uniq } from "lodash";
import { IMergeMap } from "../../pages/GraphView/GraphView";
import { ICytoscapeDataModel, ICytoscapeDataModelReference } from "../slices/querySlice";

export const defaultNodeSize = 8;

export const transformForCytoscape = (payload: {
    nodes: { [key: string]: string }[],
    edges: { 
        type?: string,
        relationship?: string
        source?: string,
        target?: string,
        destinationId?: string,
        sourceId?: string,
        createdAt: string,
        url: string
     }[],
    customTags: {
        entityId: string,
        name: string,
        type: string,
        value: any
     }[],
     rootIds: {
         id: string,
         score: number
     }[],
     searchQuery: string,
     position: any,
     parent: {
        [child: string]: { 
            id: string,
            label: string
        } 
     },
     merge: IMergeMap,
     excluded: string[]
}): { data: ICytoscapeDataModelReference, numberOfNodes: number } => {
    let primaryNode = '';
    const returnValue: ICytoscapeDataModelReference = { nodes: {}, edges: {} };
    const mappedNodes = mapTagsToNodes(payload.nodes, payload.customTags);
    const rootRef = keyBy((payload.rootIds || []), (rootId) => rootId.id );
    const excludeMap = keyBy((payload.excluded || []), (elementId) => elementId );
    let nodesObject: { [id: string]: cytoscape.ElementDefinition } = {};
    const necessaryParents = uniq(Object.values(payload?.parent || {})) || [];
    nodesObject = addParentNodes(nodesObject, necessaryParents);
    forEach(mappedNodes || [], (node, index) => {
        if (excludeMap[node.id]) {
            return;
        }
        let nodeId = node.identity ? ""+node.identity : node.id;
        let originalIdsDefault: string | null = null;
        let graphId = nodeId;
        if (payload?.merge?.[nodeId]) {
            originalIdsDefault = nodeId;
            nodeId = payload.merge[nodeId];
        }
        if (index === 0) {
            primaryNode = nodeId;
        }

        const label = !isNil(node.name) ? node.name : node.flagReason;
        const newNode: cytoscape.ElementDefinition = {
            group: 'nodes',
            data: {
                ...node,
                id: nodeId,
                entityId: node.id,
                entityType: node.nodeType,
                nodeType: node.nodeType,
                flagType: node.flagType || '',
                label,
                size: +node.size || defaultNodeSize,
                shape: node.shape || 'ellipse' as cytoscape.Css.NodeShape,
                relationshipCount: 0,
                originalIds: originalIdsDefault,
                popularityScore: rootRef[node.id]?.score || null
            },
            classes: `primary ${node?.nodeType?.toLowerCase()}`
        };
        if (payload?.parent?.[node.id]) {
            newNode.data.parent = payload.parent[node.id].id;
        }
        if (payload?.position?.[nodeId]) {
            newNode.position = payload.position[nodeId];
        }
        if (payload?.merge?.[""+node.identity || node.id] && nodesObject[nodeId]) {
            nodesObject[nodeId] = mapAttributesToNewNode(newNode, nodesObject[nodeId], graphId)
        } else {
            nodesObject[nodeId] = newNode;
            nodesObject[node.id] = newNode;
        }
    });
    const edgesObject: { [id: string ]: cytoscape.ElementDefinition } = {};
    forEach(payload.edges || [], (edge, index) => {
        const source = payload?.merge?.[edge.source || edge.sourceId] || edge.source || edge.sourceId;
        const target = payload?.merge?.[edge.target || edge.destinationId] || edge.target || edge.destinationId;
        const label = edge.type || edge.relationship;
        if (nodesObject[target] && nodesObject[source]) {
            const id = `${source}-${target}-${label}`;
            const inverseRelationId = `${target}-${source}-${label}`;
            if (!edgesObject[id] && !edgesObject[inverseRelationId]) {
                const newEdge: cytoscape.ElementDefinition = {
                    group: 'edges',
                    data: {
                        id,
                        source,
                        target,
                        label,
                        url: edge.url || edge.source,
                        width: 1
                    }
                }
                if (nodesObject[source]?.data) {
                    nodesObject[source].data.relationshipCount++
                }
                if (nodesObject[target]?.data) {
                    nodesObject[target].data.relationshipCount++
                }
                edgesObject[id] = newEdge;
            } else if (edgesObject[inverseRelationId]) {
                edgesObject[inverseRelationId] = {
                    ...edgesObject[inverseRelationId],
                    data: {
                        ...edgesObject[inverseRelationId].data,
                        mutualRelation: true,
                        width: 2
                    }
                }
            }
        }
    })
    returnValue.nodes = nodesObject;
    returnValue.edges = edgesObject;
    return { data: returnValue, numberOfNodes: Object.keys(nodesObject || []).length };
}

export const mapAttributesToNewNode = (newNode: cytoscape.ElementDefinition, oldNode: cytoscape.ElementDefinition, ogId: string): cytoscape.ElementDefinition => {
    return {
        data: {
            ...mergeDataValues(newNode.data, oldNode.data),
            id: newNode.data.id,
            size: newNode.data.size,
            originalIds: oldNode.data.originalIds?.length ? oldNode.data.originalIds + `, ${ogId}` : ogId
        },
        position: {
            ...newNode.position
        },
        classes: newNode.classes
    }
}

const mergeDataValues = (newData, oldData) => {
    const returnValue = { ...newData };
    for (let key in oldData) {
        if (returnValue[key] && oldData[key]?.length) {
            returnValue[key] = returnValue[key] + `, ${oldData[key]}`
        } else if (oldData[key]) {
            if (typeof oldData[key] === 'object' && Array.isArray(oldData[key]) && oldData[key]?.length) {
                if (oldData[key]?.length) {
                    returnValue[key] = oldData[key]?.join(', ');
                } else {
                    returnValue[key] = "";
                }
            } else {
                returnValue[key] = oldData[key]
            }
        }
        if (validateReturnValue(returnValue[key])) {
            returnValue[key] = uniq(returnValue[key]?.split(', ') || [])?.join(', ');
        }
    }
    return returnValue;
}

const validateReturnValue = (returnValue: any): boolean => {
    return returnValue && typeof returnValue !== 'number' && typeof returnValue !== 'boolean' && returnValue.indexOf(',') > -1
}

const addParentNodes = (nodesObject: { [id: string]: cytoscape.ElementDefinition }, necessaryParents: { id: string, label: string}[]) => {
    const returnValue = {...nodesObject};
    necessaryParents.forEach((parent: { id: string, label: string}) => {
        returnValue[parent.id] = {
                group: 'nodes',
                data: {
                    id: parent.id,
                    label: parent.label,
                    entityType: 'parent'
                },
                classes: 'parent'
            }
    })
    return returnValue;
}

export const expandAllCytoscapeData = (dataModel: ICytoscapeDataModelReference): ICytoscapeDataModel => {
    const returnValue: ICytoscapeDataModel = { nodes: [], edges: [] };
    returnValue.nodes = Object.values(dataModel.nodes);
    returnValue.edges = Object.values(dataModel.edges);
    return returnValue;
}

const mapTagsToNodes = (nodes: { [key: string]: string }[], tags: { entityId: string,name: string,type: string,value: any }[]) => {
    const uniqueTags = uniq(tags);
    return nodes.map((node) => {
        const returnValue = { ...node };
        uniqueTags.forEach((tag) => {
            if (tag.entityId === node.id) {
                returnValue[tag.name] = tag.value;
            }
        })
        return returnValue;
    });
}